import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { useLenis } from "lenis/react";
import { List, X, ArrowUpRight } from "@phosphor-icons/react";
import { useLang } from "@/LanguageContext";
import { COMPANY } from "@/content";

export default function Navbar() {
  const { t, lang, toggle } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const lenis = useLenis();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    if (lenis) lenis.scrollTo(el, { offset: -80 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  const go = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollTo(id), 350);
    } else {
      scrollTo(id);
    }
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-navy/80 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto max-w-[1400px] px-6 lg:px-12 flex items-center justify-between h-20">
        <button data-testid="logo-home" onClick={() => go("top")} className="flex flex-col items-start leading-none">
          <span className="font-serif text-2xl tracking-tight text-white">Magis</span>
          <span className="text-[10px] uppercase tracking-[0.42em] text-gold mt-0.5">Belgium</span>
        </button>

        <div className="hidden lg:flex items-center gap-9">
          {t.nav.links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => go(l.id)}
              className="link-underline text-sm text-white/70 hover:text-white transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-4">
          <button
            data-testid="lang-toggle"
            onClick={toggle}
            className="flex items-center gap-1 text-xs font-medium tracking-wider text-white/80 border border-white/15 rounded-full px-3 py-1.5 hover:border-gold hover:text-gold transition-colors duration-300"
          >
            <span className={lang === "en" ? "text-gold" : ""}>EN</span>
            <span className="text-white/30">/</span>
            <span className={lang === "fr" ? "text-gold" : ""}>FR</span>
          </button>

          <button
            data-testid="nav-cta"
            onClick={() => go("contact")}
            className="btn-gold hidden sm:inline-flex items-center gap-2 bg-gold text-navy text-sm font-semibold rounded-full pl-5 pr-4 py-2.5"
          >
            <span>{t.nav.cta}</span>
            <ArrowUpRight weight="bold" size={16} className="relative z-10" />
          </button>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden text-white p-1"
          >
            {open ? <X size={26} /> : <List size={26} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-navy/95 backdrop-blur-xl border-b border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {t.nav.links.map((l) => (
                <button
                  key={l.id}
                  onClick={() => go(l.id)}
                  className="text-left font-serif text-xl text-white/90"
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => go("contact")}
                className="mt-2 bg-gold text-navy text-sm font-semibold rounded-full px-5 py-3 text-center"
              >
                {t.nav.cta}
              </button>
              <a href={`mailto:${COMPANY.email}`} className="text-xs text-white/50 mt-2">{COMPANY.email}</a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
